import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useRoomStore } from '../../stores/room-store.js';
import { useAuthStore } from '../../stores/auth-store.js';
import { formatTime } from '../../lib/utils.js';

interface ChatPanelProps {
  onSendMessage: (text: string) => void;
}

export function ChatPanel({ onSendMessage }: ChatPanelProps) {
  const chatMessages = useRoomStore((s) => s.chatMessages);
  const user = useAuthStore((s) => s.user);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    onSendMessage(text);
    setInput('');
  }

  return (
    <div className="flex h-full flex-col bg-neutral-900 border-l border-neutral-800">
      {/* Header */}
      <div className="px-4 py-3 border-b border-neutral-800">
        <h3 className="text-sm font-semibold text-neutral-100">Chat</h3>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {chatMessages.length === 0 && (
          <p className="text-center text-xs text-neutral-600 mt-8">
            No messages yet. Say hello! 👋
          </p>
        )}
        {chatMessages.map((msg) => {
          const isOwn = msg.senderId === user?.id;
          return (
            <div key={msg.id} className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
              {!isOwn && (
                <span className="text-xs text-neutral-500 mb-1 px-1">{msg.senderName}</span>
              )}
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm break-words ${
                  isOwn
                    ? 'bg-ember-600 text-white rounded-br-sm'
                    : 'bg-neutral-800 text-neutral-100 rounded-bl-sm'
                }`}
              >
                {msg.text}
              </div>
              <span className="text-[10px] text-neutral-600 mt-0.5 px-1">
                {formatTime(msg.timestamp)}
              </span>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 px-3 py-2 border-t border-neutral-800"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Send a message…"
          maxLength={500}
          className="flex-1 rounded-lg border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-ember-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!input.trim()}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-ember-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ember-700 transition-colors"
          aria-label="Send"
        >
          ↑
        </button>
      </form>
    </div>
  );
}
